// @flow

import { currentUserSelector } from '../ducks/user/selectors'
import { Config } from './config'
import { isDev } from './app'
import { observeStore } from '../util/redux'
import LogRocket from 'logrocket'
import * as Sentry from '@sentry/browser'

/**
 * Logging
 * @module util/logger
 */

/**
 * Basic logging functionality with minimal formatting
 * @type {Object}
 */
export const logger = {
  error: (err: Object | string, meta?: any) => {
    if (isDev()) {
      /* eslint-disable no-console */
      console.error(err, meta)
      /* eslint-enable no-console */
    } else if (Config.SENTRY_DSN && Config.SENTRY_DSN.includes('http')) {
      Sentry.withScope(scope => {
        scope.setExtra('debug', false)
        scope.setExtra('meta', meta)
        scope.setExtra('fullError', err)
        scope.setExtra('tags', {
          ...(err.id ? { errorId: err.id } : {}),
          ...(err.originalCall &&
          err.originalCall.headers &&
          err.originalCall.headers.RequestId
            ? { requestId: err.originalCall.headers.RequestId }
            : {})
        })

        Sentry.captureException(err)
      })
    }
  },
  info: (msg: string, meta?: ?Object) => {
    if (isDev()) {
      /* eslint-disable no-console */
      console.info(msg, meta ? meta : '')
      /* eslint-enable no-console */
    } else {
      LogRocket.info({ msg, meta })
      Sentry.addBreadcrumb({
        message: msg,
        level: 'info',
        data: meta
      })
    }
  },
  warn: (msg: string, meta?: ?Object) => {
    if (isDev()) {
      /* eslint-disable no-console */
      console.warn(msg, meta)
      /* eslint-enable no-console */
    } else {
      LogRocket.warn(msg, meta)
      Sentry.addBreadcrumb({
        message: msg,
        level: 'warning',
        data: meta
      })
    }
  },
  debug: (msg: string, meta?: ?Object) => {
    /* eslint-disable no-console */
    console.debug(msg, meta)
    /* eslint-enable no-console */
  }
}

/**
 * @summary Initialize logging
 * @param  {string} version - App version
 * @param  {object} Store - App store (redux)
 * @return {object} - Logger
 */
export const initLogging = (
  version: string = '0.0.0',
  Store: Object
): typeof logger => {
  // Only setup remote logging if not on localhost
  if (!window.location.hostname.includes('localhost')) {
    // Init session recording
    LogRocket.init(Config.LOG_ROCKET_KEY)

    // Setup sentry
    Sentry.init({
      dsn: Config.SENTRY_DSN,
      release: version
    })

    Sentry.configureScope(scope => {
      scope.setExtra('sessionURL', LogRocket.sessionURL)
    })

    // Connect user id to logs
    observeStore(Store, currentUserSelector, user => {
      if (user && user.id) {
        Sentry.configureScope(scope => {
          scope.setUser({
            id: user.id,
            email: user.emailAddress
          })
        })
        LogRocket.identify(user.id, {
          email: user.emailAddress,
          firstName: user.firstName,
          lastName: user.lastName
        })
      } else {
        Sentry.configureScope(scope => {
          scope.setUser()
        })
      }
    })
  }

  return logger
}

/*eslint-enable*/
export default console
